import { Location } from '@angular/common';
import { computed, DestroyRef, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';

/** Index is the level: nothing open, the map, the filters sheet on the map. */
const LEVEL_FRAGMENTS = [undefined, 'map', 'map-filters'] as const;
type Level = 0 | 1 | 2;

/** How many entries directly below and including this one were pushed by opening a level. */
const PUSHED_ENTRIES_KEY = 'vpMapPushedEntries';

const levelOf = (fragment: string | null): Level => {
	const level = LEVEL_FRAGMENTS.indexOf(
		fragment as (typeof LEVEL_FRAGMENTS)[number],
	);
	return Math.max(level, 0) as Level;
};

/**
 * Keeps the open state of the map and its mobile filters sheet in the url
 * fragment, so the browser's back and forward buttons close and reopen them.
 */
export function injectMapFragment() {
	const router = inject(Router);
	const location = inject(Location);
	const route = inject(ActivatedRoute);
	const fragment = toSignal(route.fragment, { initialValue: null });
	const level = computed(() => levelOf(fragment()));

	// History traversals are async, so a close right after another close
	// (Escape closes the sheet and the map) must count from where the first
	// traversal started, not from an entry the browser may already be on
	let pending: { steps: number; pushedEntries: number } | null = null;
	// A close that needs a replace instead of a step back waits for that traversal
	let closeAfterPending: Level | null = null;

	const pushedEntries = (): number => {
		const state = location.getState() as Record<string, unknown> | null;
		const pushed = state?.[PUSHED_ENTRIES_KEY];
		return typeof pushed === 'number' ? pushed : 0;
	};

	const navigateTo = (target: Level, replaceUrl: boolean) =>
		router.navigate([], {
			fragment: LEVEL_FRAGMENTS[target],
			queryParamsHandling: 'preserve',
			replaceUrl,
			scroll: 'manual',
			state: replaceUrl
				? undefined
				: { [PUSHED_ENTRIES_KEY]: pushedEntries() + 1 },
		});

	const open = (target: Level) => {
		if (pending || level() !== target - 1) return;
		navigateTo(target, false);
	};

	const close = (target: Level) => {
		const pendingSteps = pending?.steps ?? 0;
		const steps = level() - pendingSteps - target;
		if (steps <= 0) return;

		const pushed = pending?.pushedEntries ?? pushedEntries();
		// Stepping back keeps the history free of entries a back press would
		// land on without visibly changing anything
		if (pushed - pendingSteps >= steps) {
			pending = { steps: pendingSteps + steps, pushedEntries: pushed };
			location.historyGo(-steps);
		} else if (pending) {
			closeAfterPending = target;
		} else {
			navigateTo(target, true);
		}
	};

	const subscription = route.fragment.subscribe(() => {
		pending = null;
		if (closeAfterPending !== null) {
			const target = closeAfterPending;
			closeAfterPending = null;
			close(target);
		}
	});
	inject(DestroyRef).onDestroy(() => subscription.unsubscribe());

	return {
		mapOpen: computed(() => level() >= 1),
		filtersOpen: computed(() => level() === 2),
		openMap: () => open(1),
		openFilters: () => open(2),
		closeMap: () => close(0),
		closeFilters: () => close(1),
	};
}
